import { useState } from 'react';
import { Alert as MuiAlert, Box, Button, CircularProgress, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import { NotificationsActive, Refresh } from '@mui/icons-material';
import { createFileRoute } from '@tanstack/react-router';
import { ProductRouteGuard } from '@/components/ProductRouteGuard';
import { useMutation, useQuery } from '@tanstack/react-query';
import * as cuitProfilesApi from '@/api/cuitProfiles';
import * as afipApi from '@/api/afipIntegrations';
import { toApiError } from '@/api/client';
import { LoadingScreen } from '@/components/LoadingScreen';

export const Route = createFileRoute('/_authenticated/alerts/category-change')({
  component: CategoryChangeAlertRoute,
});


function CategoryChangeAlertRoute(): JSX.Element {
  return (
    <ProductRouteGuard
      products={['Estudio', 'Empresa']}
      requirePermission="canManageAfip"
      title="Alerta de categoría no disponible"
      description="Seleccioná un producto fiscal con permisos ARCA para consultar cambios de categoría."
    >
      <CategoryChangeAlertPage />
    </ProductRouteGuard>
  );
}

function CategoryChangeAlertPage(): JSX.Element {
  const profilesQ = useQuery({ queryKey: ['cuit-profiles', 'category-alert'], queryFn: () => cuitProfilesApi.listCuitProfiles(0, 200) });
  const [profileId, setProfileId] = useState('');
  const checkMut = useMutation({ mutationFn: () => afipApi.checkCategory(profileId) });

  if (profilesQ.isLoading) return <LoadingScreen message="Cargando contribuyentes…" />;
  const profiles = profilesQ.data?.items ?? [];

  return (
    <Box sx={{ maxWidth: 820 }}>
      <Stack spacing={3}>
        <Stack direction="row" spacing={2} alignItems="center">
          <NotificationsActive color="primary" sx={{ fontSize: 38 }} />
          <Box>
            <Typography variant="h4">Alerta cambio categoría</Typography>
            <Typography variant="body2" color="text.secondary">Consultá el padrón de ARCA y detectá cambios en la condición fiscal guardada.</Typography>
          </Box>
        </Stack>

        <MuiAlert severity="info">
          La consulta es real contra la constancia de inscripción de ARCA. El primer control compara el dato guardado en Tilde; los cambios posteriores generan una alerta en el Centro de Alertas.
        </MuiAlert>

        {profiles.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Typography>No hay contribuyentes configurados. Agregá un CUIT para consultar su padrón.</Typography>
          </Paper>
        ) : (
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Stack spacing={2}>
              <TextField select label="Contribuyente" value={profileId} onChange={(e) => setProfileId(e.target.value)} fullWidth>
                <MenuItem value="">Seleccionar CUIT</MenuItem>
                {profiles.map((profile) => <MenuItem key={profile.id} value={profile.id}>{profile.razonSocial} · {profile.cuit}</MenuItem>)}
              </TextField>
              <Button
                variant="contained"
                startIcon={checkMut.isPending ? <CircularProgress size={18} color="inherit" /> : <Refresh />}
                disabled={!profileId || checkMut.isPending}
                onClick={() => checkMut.mutate()}
              >
                Consultar padrón de ARCA
              </Button>
              {checkMut.error ? <MuiAlert severity="error">{toApiError(checkMut.error).message}</MuiAlert> : null}
              {checkMut.data ? (
                <MuiAlert severity={checkMut.data.changed ? 'warning' : 'success'}>
                  {checkMut.data.changed
                    ? `Cambio detectado: ${checkMut.data.previousCondition} → ${checkMut.data.currentCondition}. Se creó una alerta.`
                    : `Sin cambios. ARCA informa ${checkMut.data.currentCondition}.`}
                  {checkMut.data.categoriaMonotributo ? ` Categoría: ${checkMut.data.categoriaMonotributo}.` : ''}
                </MuiAlert>
              ) : null}
            </Stack>
          </Paper>
        )}
      </Stack>
    </Box>
  );
}
